<template>
    <div class="flex flex-col gap-8">
        <!-- Seletor global de período + botão de gerar dossiê -->
        <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex flex-wrap items-center gap-3">
                <span class="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Janela de análise
                </span>
                <div
                    role="group"
                    aria-label="Selecionar período de análise"
                    class="inline-flex items-center gap-0.5 rounded-full bg-gray-100 p-1 dark:bg-gray-800"
                >
                    <button
                        v-for="option in periodOptions"
                        :key="option"
                        type="button"
                        :aria-pressed="period === option"
                        @click="period = option"
                        :class="[
                            'rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
                            period === option
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                        ]"
                    >
                        {{ option }}d
                    </button>
                </div>
                <span class="text-xs text-gray-400 dark:text-gray-500">
                    {{ periodRangeLabel }}
                </span>
            </div>

            <button
                @click="generateDossier"
                :disabled="generatingDossier"
                class="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition duration-150 ease-in-out hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <svg
                    v-if="!generatingDossier"
                    class="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                </svg>
                <svg
                    v-else
                    class="-ml-1 mr-3 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
                {{ generatingDossier ? "Gerando..." : "Gerar Dossiê" }}
            </button>
        </div>

        <!-- Bloco temporal: comissões na janela selecionada -->
        <section aria-labelledby="commissions-heading" class="flex flex-col gap-4">
            <div class="flex items-center gap-2">
                <h2
                    id="commissions-heading"
                    class="text-base font-semibold text-gray-900 dark:text-white"
                >
                    Comissões
                </h2>
                <span
                    class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                >
                    Últimos {{ period }} dias
                </span>
            </div>

            <commission-cards :user="user" :period="period"></commission-cards>

            <commissions-chart
                :user="user"
                :filters="chartFilters"
                :loading="loading"
                :period="period"
            />
        </section>

        <div class="mb-4 mt-4 grid grid-cols-3 gap-3">
            <chargeback-metric
                v-for="metric in chargebackMetrics"
                :key="metric.name"
                :metric="metric"
                :loading="chargebackLoading"
            />
        </div>

        <!-- <monthly-chargeback-rate :user="user"/> -->

        <!-- Cards organizados e colapsáveis -->
        <div
            class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
        >
            <collapsible-info-card
                title="Score"
                icon="StarIcon"
                :collapsed="false"
                :priority="scorePriority"
                :summary="scoreSummary"
                :badge="scoreBadge"
                :loading="scoreSummary === ''"
            >
                <score :user="user" @data-loaded="updateScoreSummary" />
            </collapsible-info-card>
            <collapsible-info-card
                title="Análise do Sistema"
                icon="ExclamationCircleIcon"
                :collapsed="!userAnalysis"
                :priority="systemAnalysisPriority"
                :summary="systemAnalysisSummary"
                :badge="systemAnalysisBadge"
                :loading="!userAnalysis"
            >
                <user-system-analysis
                    :user="user"
                    @data-loaded="updateSystemAnalysisSummary"
                />
            </collapsible-info-card>

            <collapsible-info-card
                title="BINs de Cartão Mais Usados"
                icon="CreditCardIcon"
                :collapsed="true"
                :summary="binSummary"
                :priority="binPriority"
                :badge="binBadge"
                :period-label="`Últimos ${period} dias`"
                :loading="binSummary === ''"
            >
                <user-bin-usage-card
                    :user="user"
                    :period="period"
                    @data-loaded="updateBinSummary"
                />
            </collapsible-info-card>

            <collapsible-info-card
                title="Métodos de Pagamento"
                icon="BanknotesIcon"
                :collapsed="true"
                :summary="paymentMethodSummary"
                :priority="paymentMethodPriority"
                :badge="paymentMethodBadge"
                :period-label="`Últimos ${period} dias`"
                :loading="paymentMethodSummary === ''"
            >
                <user-payment-method-card
                    :user="user"
                    :period="period"
                    @data-loaded="updatePaymentMethodSummary"
                />
            </collapsible-info-card>

            <collapsible-info-card
                title="Tipos de Comissão"
                icon="ChartBarIcon"
                :collapsed="true"
                :summary="commissionTypesSummary"
                :priority="commissionTypesPriority"
                :badge="commissionTypesBadge"
                :loading="commissionTypesSummary === ''"
            >
                <user-commission-types-card
                    :user="user"
                    @data-loaded="updateCommissionTypesSummary"
                />
            </collapsible-info-card>
        </div>
    </div>
</template>

<script>
import TableComponent from "../../../components/layouts/table/TableComponent.vue";
import CommissionCards from "../../../components/user/CommissionCards.vue";
import Score from "../../../components/user/Score.vue";
import ChargebackMetric from "../../../components/user/metrics/ChargebackMetric.vue";
import MonthlyChargebackRate from "../../../components/user/metrics/MonthlyChargebackRate.vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import CommissionsChart from "../../users/commissions/CommissionsChart.vue";
import UserBinUsageCard from "../../../components/user/UserBinUsageCard.vue";
import {
    ExclamationCircleIcon,
    CreditCardIcon,
    BanknotesIcon,
    ChartBarIcon,
    StarIcon,
} from "@heroicons/vue/24/outline";
import UserSystemAnalysis from "../../../components/user/UserSystemAnalysis.vue";
import UserPaymentMethodCard from "../../../components/user/UserPaymentMethodCard.vue";
import UserCommissionTypesCard from "../../../components/user/UserCommissionTypesCard.vue";
import CollapsibleInfoCard from "../../../components/user/CollapsibleInfoCard.vue";

export default {
    components: {
        CommissionsChart,
        MonthlyChargebackRate,
        ChargebackMetric,
        Score,
        CommissionCards,
        TableComponent,
        UserBinUsageCard,
        ExclamationCircleIcon,
        CreditCardIcon,
        BanknotesIcon,
        ChartBarIcon,
        StarIcon,
        UserSystemAnalysis,
        UserPaymentMethodCard,
        UserCommissionTypesCard,
        CollapsibleInfoCard,
    },
    props: ["user"],
    data() {
        return {
            loading: true,
            next_page_url: false,
            chargebackMetrics: [],
            chargebackLoading: true,
            metrics: [],
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 11,
                from: 1,
                to: 11,
            },
            filters: {
                text: "",
                status: [],
                payment_method: [],
            },
            // Janela de análise aplicada a todas as métricas temporais da tela
            period: 30,
            periodOptions: [7, 15, 30, 90],
            // Dados para os summaries dos cards
            userAnalysis: null,
            scoreSummary: "",
            scorePriority: "normal",
            scoreBadge: null,
            binSummary: "",
            binPriority: "normal",
            binBadge: null,
            paymentMethodSummary: "",
            paymentMethodPriority: "normal",
            paymentMethodBadge: null,
            systemAnalysisSummary: "",
            systemAnalysisPriority: "normal",
            systemAnalysisBadge: null,
            commissionTypesSummary: "",
            commissionTypesPriority: "normal",
            commissionTypesBadge: null,
            // Estado do botão de gerar dossiê
            generatingDossier: false,
        };
    },

    mixins: [HelperMixin],

    computed: {
        chartFilters() {
            return {
                ...this.filters,
                interval: this.period,
            };
        },

        periodRangeLabel() {
            const start = new Date();
            start.setDate(start.getDate() - this.period);

            const format = (date) =>
                date.toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                });

            return `${format(start)} – ${format(new Date())}`;
        },
    },

    watch: {
        period() {
            // Evita misturar valores de janelas diferentes: some o resumo até o novo chegar
            this.binSummary = "";
            this.paymentMethodSummary = "";

            const userId = this.$route.params.user_id ?? this.user.id;
            this.fetchChargebacks(userId);
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;

            const userId = this.$route.params.user_id ?? this.user.id;

            axios
                .get("/api/user/" + userId + "/metrics")
                .then(({ data }) => {
                    this.metrics = data;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                });

            this.fetchChargebacks(userId);
        },

        fetchChargebacks(userId) {
            this.chargebackLoading = true;

            axios
                .get("/api/user/" + userId + "/metrics/chargebacks", {
                    params: {
                        interval: this.period,
                    },
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                })
                .then(({ data }) => {
                    let metrics = [];
                    metrics.push(
                        {
                            name: "Competência",
                            tpn: data.tpn_comp,
                            tpv: data.tpv_comp,
                        },
                        {
                            name: "Caixa",
                            tpn: data.tpn_box,
                            tpv: data.tpv_box,
                        },
                        {
                            name: "Global",
                            tpn: data.tpn_global,
                            tpv: data.tpv_global,
                        },
                    );
                    this.chargebackMetrics = metrics;
                })
                .finally(() => {
                    this.chargebackLoading = false;
                });
        },

        updateScoreSummary(scoreData) {
            if (!scoreData) {
                this.scoreSummary = "Score não disponível";
                this.scorePriority = "normal";
                this.scoreBadge = null;
                return;
            }

            const { amount, level, bypass, historical } = scoreData;

            // Determina a prioridade baseada no score
            if (amount < 200) {
                this.scorePriority = "danger";
            } else if (amount < 400) {
                this.scorePriority = "warning";
            } else {
                this.scorePriority = "normal";
            }

            // Badge com informações especiais
            let badgeText = level.charAt(0).toUpperCase() + level.slice(1);
            if (bypass) {
                badgeText = "Bypass ativo";
                this.scorePriority = "warning";
            } else if (historical) {
                badgeText = "Histórico";
            }

            this.scoreBadge = badgeText;
            this.scoreSummary = `Score ${amount} (${level})${bypass ? " - Bypass ativo" : ""}${historical ? " - Visualizando histórico" : ""}`;
        },

        updateBinSummary(data) {
            if (!data || !data.top_bins || data.top_bins.length === 0) {
                this.binSummary = "Nenhum BIN encontrado";
                this.binPriority = "normal";
                this.binBadge = null;
                return;
            }

            const topBins = data.top_bins;
            const summary = data.summary;

            // Determina prioridade baseada no resumo geral se disponível
            if (summary && summary.high_fraud_bins > 0) {
                this.binPriority = "danger";
                this.binBadge = `${summary.high_fraud_bins}/${summary.total_unique_bins} alto risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.high_fraud_bins} alto risco`;
            } else if (summary && summary.medium_fraud_bins > 0) {
                this.binPriority = "warning";
                this.binBadge = `${summary.medium_fraud_bins}/${summary.total_unique_bins} médio risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.medium_fraud_bins} médio risco`;
            } else {
                // Fallback para análise dos top bins se não houver resumo
                const highFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "high",
                ).length;
                const mediumFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "medium",
                ).length;
                const totalBins = summary?.total_unique_bins || topBins.length;

                if (highFraudBins > 0) {
                    this.binPriority = "danger";
                    this.binBadge = `${highFraudBins}/${totalBins} alto risco`;
                    this.binSummary = `${totalBins} BINs, ${highFraudBins} alto risco`;
                } else if (mediumFraudBins > 0) {
                    this.binPriority = "warning";
                    this.binBadge = `${mediumFraudBins}/${totalBins} médio risco`;
                    this.binSummary = `${totalBins} BINs, ${mediumFraudBins} médio risco`;
                } else {
                    this.binPriority = "normal";
                    this.binBadge = `${totalBins} BINs`;
                    this.binSummary = `${totalBins} BINs únicos`;
                }
            }
        },

        updatePaymentMethodSummary(data) {
            if (!data || data.length === 0) {
                this.paymentMethodSummary = "Nenhum método encontrado";
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (method) => method.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (method) => method.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Alta concentração";
                this.paymentMethodSummary = `${data.length} métodos, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Concentração média";
                this.paymentMethodSummary = `${data.length} métodos, concentração média`;
            } else {
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = `${data.length} métodos`;
                this.paymentMethodSummary = `${data.length} métodos distribuídos`;
            }
        },

        updateSystemAnalysisSummary(analysis) {
            this.userAnalysis = analysis;

            if (!analysis) {
                this.systemAnalysisSummary = "Análise não disponível";
                this.systemAnalysisPriority = "normal";
                this.systemAnalysisBadge = null;
                return;
            }

            // Determina a prioridade baseada na confiança e análise
            if (analysis.analysis === "Reprovar" || analysis.confidence < 30) {
                this.systemAnalysisPriority = "danger";
            } else if (
                analysis.analysis === "Atenção" ||
                (analysis.confidence >= 30 && analysis.confidence < 70)
            ) {
                this.systemAnalysisPriority = "warning";
            } else {
                this.systemAnalysisPriority = "normal";
            }

            this.systemAnalysisBadge = `${analysis.confidence}%`;
            this.systemAnalysisSummary = `${analysis.analysis} (${analysis.confidence}% confiança)`;
        },

        updateCommissionTypesSummary(data) {
            if (!data || data.length === 0) {
                this.commissionTypesSummary =
                    "Nenhum tipo de comissão encontrado";
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (commission) => commission.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (commission) => commission.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Alta concentração";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Concentração média";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração média`;
            } else {
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = `${data.length} tipos de comissão`;
                this.commissionTypesSummary = `${data.length} tipos de comissão distribuídos`;
            }
        },

        generateDossier() {
            this.generatingDossier = true;

            const userId = this.$route.params.user_id ?? this.user.id;

            axios
                .post(`/api/user/${userId}/dossier/generate`)
                .then((response) => {
                    this.generatingDossier = false;

                    if (response.data.success && response.data.download_url) {
                        // Abrir URL em nova aba
                        window.open(response.data.download_url, "_blank");
                    } else {
                        throw new Error("Resposta inválida do servidor");
                    }
                })
                .catch((error) => {
                    this.generatingDossier = false;

                    console.error("Erro ao gerar dossiê:", error);

                    const errorMessage =
                        error.response?.data?.message ||
                        "Erro ao gerar dossiê. Tente novamente.";
                });
        },
    },
};
</script>
